/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Input, OnChanges, SimpleChanges} from '@angular/core';

interface Entity {
  id: string;
  label: string;
  requirements?: string[];
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  color?: string;
  shape?: 'rect' | 'circle' | 'ellipse';
}

interface Connection {
  from: string;
  to: string;
  label?: string;
  style?: string;
  _index?: number; // Internal: index within a group of connections with same from/to
  _total?: number; // Internal: total connections in this group
}

interface EntityDiagramData {
  entities: Entity[];
  connections?: Connection[];
  width?: number;
  height?: number;
}

@Component({
  standalone: false,
  selector: 'entity-diagram',
  template: `
    <div *ngIf="diagramData" class="entity-diagram-container" [style.width.px]="containerWidth" [style.height.px]="containerHeight">
      <svg [attr.width]="svgWidth" [attr.height]="svgHeight" class="entity-diagram-svg">
        <!-- Connections/Relationships -->
        <g *ngIf="diagramData.connections" class="connections">
          <g *ngFor="let connection of diagramData.connections">
            <path
              [attr.d]="getConnectionPath(connection)"
              [attr.stroke]="connection.style || '#666'"
              fill="none"
              stroke-width="2"
              marker-end="url(#arrowhead)"
              class="connection-line"/>
            <text
              *ngIf="connection.label"
              [attr.x]="getConnectionLabelX(connection)"
              [attr.y]="getConnectionLabelY(connection)"
              text-anchor="middle"
              font-size="12"
              fill="#333"
              class="connection-label">
              {{connection.label}}
            </text>
          </g>
        </g>

        <!-- Arrow marker definition -->
        <defs>
          <marker
            id="arrowhead"
            markerWidth="10"
            markerHeight="10"
            refX="9"
            refY="3"
            orient="auto">
            <polygon points="0 0, 10 3, 0 6" fill="#666"/>
          </marker>
        </defs>

        <!-- Entities -->
        <g class="entities">
          <g *ngFor="let entity of diagramData.entities" [attr.transform]="'translate(' + entity.x + ',' + entity.y + ')'">
            <!-- Rectangle shape -->
            <rect
              *ngIf="!entity.shape || entity.shape === 'rect'"
              [attr.width]="getEntityWidth(entity)"
              [attr.height]="getEntityHeight(entity)"
              [attr.fill]="entity.color || '#4a90e2'"
              [attr.stroke]="'#2c5aa0'"
              stroke-width="2"
              rx="4"
              class="entity-shape"/>

            <!-- Circle shape -->
            <circle
              *ngIf="entity.shape === 'circle'"
              [attr.r]="getEntityWidth(entity) / 2"
              [attr.fill]="entity.color || '#4a90e2'"
              [attr.stroke]="'#2c5aa0'"
              stroke-width="2"
              class="entity-shape"/>

            <!-- Ellipse shape -->
            <ellipse
              *ngIf="entity.shape === 'ellipse'"
              [attr.rx]="getEntityWidth(entity) / 2"
              [attr.ry]="getEntityHeight(entity) / 2"
              [attr.fill]="entity.color || '#4a90e2'"
              [attr.stroke]="'#2c5aa0'"
              stroke-width="2"
              class="entity-shape"/>

            <!-- Entity label -->
            <text
              [attr.x]="getEntityWidth(entity) / 2"
              [attr.y]="20"
              text-anchor="middle"
              dominant-baseline="middle"
              font-size="14"
              font-weight="500"
              fill="#fff"
              class="entity-label">
              {{entity.label}}
            </text>

            <!-- Requirements as sub-boxes -->
            <g *ngIf="entity.requirements && entity.requirements.length > 0" class="requirements">
              <g *ngFor="let requirement of entity.requirements; let i = index">
                <rect
                  [attr.x]="8"
                  [attr.y]="30 + i * 22"
                  [attr.width]="getEntityWidth(entity) - 16"
                  [attr.height]="18"
                  fill="#fff"
                  fill-opacity="0.2"
                  stroke="#fff"
                  stroke-width="1"
                  rx="2"
                  class="requirement-box"/>
                <text
                  [attr.x]="getEntityWidth(entity) / 2"
                  [attr.y]="30 + i * 22 + 12"
                  text-anchor="middle"
                  dominant-baseline="middle"
                  font-size="10"
                  fill="#fff"
                  class="requirement-label">
                  {{requirement}}
                </text>
              </g>
            </g>
          </g>
        </g>
      </svg>
    </div>
  `,
  styles: [`
    .entity-diagram-container {
      margin: 20px 0;
      overflow: auto;
      border: 1px solid #ddd;
      border-radius: 4px;
      background: #fafafa;
    }
    .entity-diagram-svg {
      display: block;
    }
    .entity-shape {
      cursor: pointer;
      transition: opacity 0.2s;
    }
    .entity-shape:hover {
      opacity: 0.8;
    }
    .connection-label {
      pointer-events: none;
    }
  `]
})
export class EntityDiagramComponent implements OnChanges {
  @Input() data: string | EntityDiagramData;

  diagramData: EntityDiagramData | null = null;
  svgWidth = 800;
  svgHeight = 600;
  containerWidth = 800;
  containerHeight = 600;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      this.parseData();
    }
  }

  private parseData(): void {
    if (!this.data) {
      this.diagramData = null;
      return;
    }

    let parsed: EntityDiagramData;

    if (typeof this.data === 'string') {
      try {
        parsed = JSON.parse(this.data);
      } catch {
        this.diagramData = null;
        return;
      }
    } else {
      parsed = this.data;
    }

    // Process connections first to analyze the graph
    if (parsed.connections && parsed.connections.length > 0) {
      this.processConnections(parsed);
    }

    // Auto-layout if positions not provided or if we need to optimize for multiple connections
    if (parsed.entities && parsed.entities.length > 0) {
      const hasPositions = parsed.entities.some(e => e.x !== undefined && e.y !== undefined);

      if (!hasPositions || this.needsLayoutOptimization(parsed)) {
        this.smartLayout(parsed);
      }
    }

    // Calculate SVG dimensions
    if (parsed.width && parsed.height) {
      this.svgWidth = parsed.width;
      this.svgHeight = parsed.height;
      this.containerWidth = parsed.width;
      this.containerHeight = parsed.height;
    } else if (parsed.entities && parsed.entities.length > 0) {
      this.calculateDimensions(parsed);
    }

    this.diagramData = parsed;
  }

  private needsLayoutOptimization(data: EntityDiagramData): boolean {
    if (!data.connections || data.connections.length === 0) return false;

    // Check if there are multiple connections from the same source to the same target
    const connectionGroups = new Map<string, number>();
    data.connections.forEach(conn => {
      const key = `${conn.from}->${conn.to}`;
      connectionGroups.set(key, (connectionGroups.get(key) || 0) + 1);
    });

    // If any source->target pair has multiple connections, optimize layout
    return Array.from(connectionGroups.values()).some(count => count > 1);
  }

  private smartLayout(data: EntityDiagramData): void {
    const entities = data.entities;
    const connections = data.connections || [];
    const entityMap = new Map(entities.map(e => [e.id, e]));

    // Find connections where same source connects to same target multiple times
    const connectionGroups = new Map<string, Connection[]>();
    connections.forEach(conn => {
      const key = `${conn.from}->${conn.to}`;
      if (!connectionGroups.has(key)) {
        connectionGroups.set(key, []);
      }
      const group = connectionGroups.get(key);
      if (group) {
        group.push(conn);
      }
    });

    // Find target entities that receive multiple connections from same source
    const multiConnectionTargets = new Map<string, {source: string, count: number}>();
    connectionGroups.forEach((group, key) => {
      if (group.length > 1) {
        const [fromId, toId] = key.split('->');
        multiConnectionTargets.set(toId, {source: fromId, count: group.length});
      }
    });

    const horizontalSpacing = 280;
    const verticalSpacing = 200;
    const startX = 50;
    const startY = 50;
    const baseWidth = 120;

    if (multiConnectionTargets.size > 0) {
      // Use optimized layout for multiple connections
      const positioned = new Set<string>();
      let currentY = startY;

      // Process each target with multiple connections from same source
      multiConnectionTargets.forEach((info, targetId) => {
        const targetEntity = entityMap.get(targetId);
        const sourceEntity = entityMap.get(info.source);

        if (!targetEntity || !sourceEntity) return;

        // Position source on the left, target on the right
        // But offset them vertically to allow connections to spread
        const connectionCount = info.count;
        const verticalOffset = (connectionCount - 1) * 30; // Space for multiple connections

        sourceEntity.x = startX;
        sourceEntity.y = currentY + verticalOffset / 2;
        sourceEntity.width = sourceEntity.width || baseWidth;
        positioned.add(info.source);

        targetEntity.x = startX + horizontalSpacing * 2;
        targetEntity.y = currentY;
        targetEntity.width = targetEntity.width || baseWidth;
        positioned.add(targetId);

        currentY += verticalSpacing + verticalOffset;
      });

      // Position remaining entities in a grid
      let gridX = startX;
      let gridY = currentY;
      const maxPerRow = 4;
      let rowCount = 0;

      entities.forEach(entity => {
        if (!positioned.has(entity.id)) {
          entity.x = gridX;
          entity.y = gridY;
          entity.width = entity.width || baseWidth;
          positioned.add(entity.id);

          gridX += horizontalSpacing;
          rowCount++;
          if (rowCount >= maxPerRow) {
            gridX = startX;
            gridY += verticalSpacing;
            rowCount = 0;
          }
        }
      });
    } else {
      // Fallback to simple grid layout
      this.autoLayout(data);
    }
  }

  private autoLayout(data: EntityDiagramData): void {
    const entities = data.entities;
    const cols = Math.ceil(Math.sqrt(entities.length));
    const spacing = 200;
    const startX = 50;
    const startY = 50;

    entities.forEach((entity, index) => {
      const row = Math.floor(index / cols);
      const col = index % cols;
      entity.x = startX + col * spacing;
      entity.y = startY + row * spacing;
      entity.width = entity.width || 120;
      // Height will be calculated dynamically based on requirements
    });
  }

  private calculateDimensions(data: EntityDiagramData): void {
    if (!data.entities || data.entities.length === 0) {
      return;
    }

    const maxX = Math.max(...data.entities.map(e => (e.x || 0) + this.getEntityWidth(e)));
    const maxY = Math.max(...data.entities.map(e => (e.y || 0) + this.getEntityHeight(e)));

    this.svgWidth = Math.max(maxX + 50, 800);
    this.svgHeight = Math.max(maxY + 50, 600);
    this.containerWidth = Math.min(this.svgWidth, 1200);
    this.containerHeight = Math.min(this.svgHeight, 800);
  }

  getEntityX(entityId: string): number {
    const entity = this.diagramData?.entities.find(e => e.id === entityId);
    if (!entity) return 0;
    const width = this.getEntityWidth(entity);
    return (entity.x || 0) + width / 2;
  }

  getEntityY(entityId: string): number {
    const entity = this.diagramData?.entities.find(e => e.id === entityId);
    if (!entity) return 0;
    const height = this.getEntityHeight(entity);
    return (entity.y || 0) + height / 2;
  }

  getEntityWidth(entity: Entity): number {
    return entity.width || 120;
  }

  getEntityHeight(entity: Entity): number {
    if (entity.height) {
      return entity.height;
    }
    // Calculate height based on requirements
    const baseHeight = 40;
    const requirementHeight = entity.requirements ? entity.requirements.length * 22 : 0;
    return baseHeight + requirementHeight;
  }

  private processConnections(data: EntityDiagramData): void {
    if (!data.connections) return;

    // Group connections by from/to pair
    const connectionGroups = new Map<string, Connection[]>();

    data.connections.forEach(conn => {
      const key = `${conn.from}->${conn.to}`;
      if (!connectionGroups.has(key)) {
        connectionGroups.set(key, []);
      }
      const group = connectionGroups.get(key);
      if (group) {
        group.push(conn);
      }
    });

    // Assign indices to connections in each group
    connectionGroups.forEach((connections) => {
      const total = connections.length;
      connections.forEach((conn, index) => {
        conn._total = total;
        conn._index = index;
      });
    });
  }

  getConnectionPath(connection: Connection): string {
    const fromX = this.getEntityX(connection.from);
    const fromY = this.getEntityY(connection.from);
    const toX = this.getEntityX(connection.to);
    const toY = this.getEntityY(connection.to);

    const total = connection._total || 1;
    const index = connection._index || 0;

    // If only one connection, use straight line
    if (total === 1) {
      return `M ${fromX} ${fromY} L ${toX} ${toY}`;
    }

    // For multiple connections, use curved paths with offsets
    // Increase spacing for better visibility
    const offsetSpacing = 25; // Space between parallel connections
    const offset = (index - (total - 1) / 2) * offsetSpacing;

    // Calculate perpendicular offset direction
    const dx = toX - fromX;
    const dy = toY - fromY;
    const length = Math.sqrt(dx * dx + dy * dy);

    if (length === 0) {
      // Same position, use vertical offset
      return `M ${fromX} ${fromY} L ${toX} ${toY + offset}`;
    }

    const perpX = -dy / length * offset;
    const perpY = dx / length * offset;

    // Control points for quadratic bezier curve
    // Use a stronger curve for better separation
    const curveStrength = 0.4; // How much the curve bends
    const midX = (fromX + toX) / 2;
    const midY = (fromY + toY) / 2;
    const controlX = midX + perpX * (1 + curveStrength);
    const controlY = midY + perpY * (1 + curveStrength);

    return `M ${fromX} ${fromY} Q ${controlX} ${controlY} ${toX} ${toY}`;
  }

  getConnectionLabelX(connection: Connection): number {
    const fromX = this.getEntityX(connection.from);
    const toX = this.getEntityX(connection.to);
    const total = connection._total || 1;
    const index = connection._index || 0;

    if (total === 1) {
      return (fromX + toX) / 2;
    }

    // Position label along the curve
    const offsetSpacing = 20;
    const offset = (index - (total - 1) / 2) * offsetSpacing;
    const fromY = this.getEntityY(connection.from);
    const toY = this.getEntityY(connection.to);
    const dx = toX - fromX;
    const dy = toY - fromY;
    const length = Math.sqrt(dx * dx + dy * dy);
    const perpX = -dy / length * offset;

    const midX = (fromX + toX) / 2;
    return midX + perpX;
  }

  getConnectionLabelY(connection: Connection): number {
    const fromY = this.getEntityY(connection.from);
    const toY = this.getEntityY(connection.to);
    const total = connection._total || 1;
    const index = connection._index || 0;

    if (total === 1) {
      return (fromY + toY) / 2 - 5;
    }

    // Position label along the curve
    const offsetSpacing = 20;
    const offset = (index - (total - 1) / 2) * offsetSpacing;
    const fromX = this.getEntityX(connection.from);
    const toX = this.getEntityX(connection.to);
    const dx = toX - fromX;
    const dy = toY - fromY;
    const length = Math.sqrt(dx * dx + dy * dy);
    const perpY = dx / length * offset;

    const midY = (fromY + toY) / 2;
    return midY + perpY - 5;
  }
}
